import axiosInstance from "@/services/https/global";
import axios from "axios";
import { Store } from "redux/Store";
import CONFIG from "@/config";
import { normalizePaged } from "@/utils/normalize-paged";

// Single source of truth for the base path.
// Confirmed by the integration spec (v1.0–1.2): the surface lives under
// /virtual-account/api/v1/admin/ledger on the virtual-account-acquiring-service.
const baseUrl = `${CONFIG.BASE_URL}/virtual-account/api/v1/admin/ledger`;

// This admin surface expects the user JWT with the `serial` scheme — NOT
// `Bearer` (which is reserved for service-to-service tokens and is rejected
// here with 401 "Invalid Token Format"). The global interceptor injects the
// raw token only when Authorization is unset, so we pre-set it per request
// with the correct prefix and it is left untouched. See spec v1.1.
const withSerialAuth = (config = {}) => {
  const token = Store.getState()?.auth?.accessToken;
  if (!token) return config;
  // Tokens are stored raw; strip any scheme that slipped in, then prefix.
  const jwt = token.replace(/^(serial|Bearer)\s+/i, "");
  return {
    ...config,
    headers: { ...(config.headers || {}), Authorization: `serial ${jwt}` },
  };
};

// Normalises thrown errors so callers can read both `.message` (for toasts,
// matching your existing pattern) and `.statusCode` (to target inline field errors).
const toError = (error) => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    const base =
      typeof data === "object" && data !== null ? data : { message: data };
    return { ...base, statusCode: error.response?.status };
  }
  return { message: "An unexpected error occurred" };
};


// GET /accounts  (paged list — backend says this exists / is coming)
export const listLedgerAccounts = async ({
  environment,
  page = 0,
  size = 10,
  search,
  status,
} = {}) => {
  const params = { environment, page, size };
  if (search && search.trim()) params.search = search.trim();
  if (status) params.status = status;

  try {
    const res = await axiosInstance.get(
      `${baseUrl}/accounts`,
      withSerialAuth({ params }),
    );
    return normalizePaged(res.data, { page, size });
  } catch (error) {
    throw toError(error);
  }
};

// GET /accounts/{accountNumber}?environment=LIVE
export const getLedgerAccount = async ({ accountNumber, environment }) => {
  try {
    const res = await axiosInstance.get(
      `${baseUrl}/accounts/${accountNumber}`,
      withSerialAuth({ params: { environment } }),
    );
    return res.data;
  } catch (error) {
    throw toError(error);
  }
};

// POST /accounts
export const createLedgerAccount = async (payload) => {
  try {
    const res = await axiosInstance.post(
      `${baseUrl}/accounts`,
      payload,
      withSerialAuth(),
    );
    return res.data;
  } catch (error) {
    throw toError(error);
  }
};

// POST /journals
export const postJournal = async (payload) => {
  try {
    const res = await axiosInstance.post(
      `${baseUrl}/journals`,
      payload,
      withSerialAuth(),
    );
    return res.data;
  } catch (error) {
    throw toError(error);
  }
};

// GET /accounts/{accountNumber}/entries?environment=LIVE&page=0&size=50
export const getLedgerEntries = async ({
  accountNumber,
  environment,
  page = 0,
  size = 50,
}) => {
  try {
    const res = await axiosInstance.get(
      `${baseUrl}/accounts/${accountNumber}/entries`,
      withSerialAuth({ params: { environment, page, size } }),
    );
    return normalizePaged(res.data, { page, size });
  } catch (error) {
    throw toError(error);
  }
};
